"use client";

import { useState } from "react";
import { 
  Headphones, 
  PhoneCall, 
  ShieldCheck, 
  Sparkles, 
  TrendingUp, 
  Clock, 
  Users, 
  Zap, 
  CheckCircle2, 
  ArrowRight 
} from "lucide-react";
import { TELECALLING_CAMPAIGNS, calculateIndicativeRetainer } from "@/lib/demos/telecallingData";

interface TelecallingHeroProps {
  currency: "INR" | "USD";
  onOpenProposalModal: (initialData: {
    campaignId: string;
    seatsCount: number;
    shiftType: "day" | "night" | "247";
    monthlyRetainer: number;
  }) => void;
}

export function TelecallingHero({ currency, onOpenProposalModal }: TelecallingHeroProps) {
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>("b2b-outbound");
  const [seatsCount, setSeatsCount] = useState<number>(6);
  const [shiftType, setShiftType] = useState<"day" | "night" | "247">("day");

  const activeCampaign = TELECALLING_CAMPAIGNS.find((c) => c.id === selectedCampaignId) || TELECALLING_CAMPAIGNS[0];
  const calculation = calculateIndicativeRetainer(selectedCampaignId, seatsCount, shiftType, currency);
  const sym = currency === "USD" ? "$" : "₹";

  return (
    <section className="relative overflow-hidden pt-12 pb-20 lg:pt-16 lg:pb-28">
      {/* Background Glows */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[650px] h-[350px] bg-gradient-to-tr from-cyan-600/15 via-blue-600/10 to-transparent blur-3xl -z-10 pointer-events-none rounded-full" />
      <div className="absolute top-1/3 -right-20 w-96 h-96 bg-cyan-500/10 blur-3xl -z-10 pointer-events-none rounded-full" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Value Proposition & Social Proof */}
          <div className="lg:col-span-6 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-semibold">
              <ShieldCheck className="w-4 h-4 text-cyan-400" />
              <span>SOC-2 Type II Certified • 99.8% Telephony Uptime SLA</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.1]">
              High-Velocity <span className="bg-gradient-to-r from-cyan-400 via-sky-300 to-blue-400 bg-clip-text text-transparent">Telecalling &amp; BPO</span> That Scales Revenue
            </h1>

            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              Deploy dedicated SDR and customer care pods in 72 hours. Equipped with AI speech analytics, real-time QA auditing, and automated CRM pipeline synchronization with instant contract generation.
            </p>

            {/* Credibility Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
              <div className="p-3 rounded-xl bg-slate-900/60 border border-slate-800">
                <div className="text-xl sm:text-2xl font-black text-cyan-400">99.8%</div>
                <div className="text-[11px] text-slate-400 uppercase tracking-wide font-medium mt-0.5">Carrier Uptime</div>
              </div>
              <div className="p-3 rounded-xl bg-slate-900/60 border border-slate-800">
                <div className="text-xl sm:text-2xl font-black text-white">&lt; 15s</div>
                <div className="text-[11px] text-slate-400 uppercase tracking-wide font-medium mt-0.5">Answer Speed</div>
              </div>
              <div className="p-3 rounded-xl bg-slate-900/60 border border-slate-800">
                <div className="text-xl sm:text-2xl font-black text-emerald-400">100%</div>
                <div className="text-[11px] text-slate-400 uppercase tracking-wide font-medium mt-0.5">Call Audited</div>
              </div>
              <div className="p-3 rounded-xl bg-slate-900/60 border border-slate-800">
                <div className="text-xl sm:text-2xl font-black text-blue-400">SOC-2</div>
                <div className="text-[11px] text-slate-400 uppercase tracking-wide font-medium mt-0.5">Type II Compliant</div>
              </div>
            </div>

            {/* Live Pipeline Sync Notice */}
            <div className="p-4 rounded-xl bg-cyan-950/40 border border-cyan-500/20 flex items-start gap-3.5">
              <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center shrink-0 mt-0.5">
                <TrendingUp className="w-4 h-4 text-cyan-400" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-cyan-300 uppercase tracking-wider">Live Dialing Floor Integration</h4>
                <p className="text-xs text-slate-300 mt-0.5">
                  Submitting a requirement instantly ingests the lead into our 6-stage Calling Floor CRM, triggering automatic Call Audit &amp; Retainer Invoice PDFs via Hostinger SMTP.
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Interactive Seat & Cost Estimator */}
          <div className="lg:col-span-6">
            <div className="relative rounded-2xl bg-slate-900/90 border border-slate-800 p-6 sm:p-8 shadow-2xl shadow-cyan-950/30 backdrop-blur-xl">
              
              {/* Card Header */}
              <div className="flex items-center justify-between border-b border-slate-800 pb-5 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                    <Headphones className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-base">BPO Pod Seat Estimator</h3>
                    <p className="text-xs text-slate-400">Real-time headcount &amp; retainer modeling</p>
                  </div>
                </div>
                <span className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                  Instant Quote
                </span>
              </div>

              {/* 1. Select Campaign Offering */}
              <div className="space-y-2 mb-6">
                <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider block">
                  Select Campaign Offering:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {TELECALLING_CAMPAIGNS.map((c) => {
                    const isSelected = c.id === selectedCampaignId;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setSelectedCampaignId(c.id)}
                        className={`p-3 rounded-xl text-left border transition-all ${
                          isSelected
                            ? "bg-cyan-500/15 border-cyan-500 text-white shadow-sm shadow-cyan-500/20"
                            : "bg-slate-950/60 border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700"
                        }`}
                      >
                        <div className="font-bold text-xs line-clamp-1">{c.title}</div>
                        <div className="text-[10px] text-cyan-400/90 mt-0.5">{c.badge}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 2. Seat Headcount Slider */}
              <div className="space-y-2 mb-6">
                <div className="flex items-center justify-between text-xs font-semibold text-slate-300">
                  <span className="uppercase tracking-wider">Dedicated Calling Seats:</span>
                  <span className="text-cyan-400 font-bold text-sm bg-cyan-500/10 px-2.5 py-0.5 rounded border border-cyan-500/20">
                    {seatsCount} SDR Callers
                  </span>
                </div>
                <input
                  type="range"
                  min="2"
                  max="30"
                  step="1"
                  value={seatsCount}
                  onChange={(e) => setSeatsCount(Number(e.target.value))}
                  className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                />
                <div className="flex justify-between text-[10px] text-slate-400 font-medium">
                  <span>2 Seats (Pilot)</span>
                  <span>10 Seats (Growth)</span>
                  <span>30 Seats (Enterprise Pod)</span>
                </div>
              </div>

              {/* 3. Shift Coverage */}
              <div className="space-y-2 mb-6">
                <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider block">
                  Shift Hours &amp; Timezone Coverage:
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setShiftType("day")}
                    className={`py-2 px-3 rounded-lg text-xs font-semibold border transition-all ${
                      shiftType === "day"
                        ? "bg-cyan-600 text-white border-cyan-400"
                        : "bg-slate-950/60 border-slate-800 text-slate-400 hover:text-white"
                    }`}
                  >
                    Day Shift (9-6)
                  </button>
                  <button
                    type="button"
                    onClick={() => setShiftType("night")}
                    className={`py-2 px-3 rounded-lg text-xs font-semibold border transition-all ${
                      shiftType === "night"
                        ? "bg-cyan-600 text-white border-cyan-400"
                        : "bg-slate-950/60 border-slate-800 text-slate-400 hover:text-white"
                    }`}
                  >
                    US / Night Shift
                  </button>
                  <button
                    type="button"
                    onClick={() => setShiftType("247")}
                    className={`py-2 px-3 rounded-lg text-xs font-semibold border transition-all ${
                      shiftType === "247"
                        ? "bg-cyan-600 text-white border-cyan-400"
                        : "bg-slate-950/60 border-slate-800 text-slate-400 hover:text-white"
                    }`}
                  >
                    24x7 Coverage
                  </button>
                </div>
              </div>

              {/* Live Financial & Volume Projections */}
              <div className="rounded-xl bg-slate-950/80 border border-slate-800 p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 divide-x divide-slate-800">
                  <div>
                    <span className="text-[10px] text-slate-400 uppercase tracking-wider block font-semibold">
                      Estimated Monthly Retainer
                    </span>
                    <div className="text-2xl font-black text-white mt-1">
                      {sym}{calculation.monthlyTotal.toLocaleString()}
                      <span className="text-xs font-normal text-slate-400 ml-1">/ month</span>
                    </div>
                    <span className="text-[10px] text-cyan-400 font-medium">
                      ({sym}{calculation.perSeatRate.toLocaleString()} / seat)
                    </span>
                  </div>

                  <div className="pl-4">
                    <span className="text-[10px] text-slate-400 uppercase tracking-wider block font-semibold">
                      Outbound Call Capacity
                    </span>
                    <div className="text-xl font-bold text-emerald-400 mt-1">
                      ~{calculation.callsTargetPerMonth.toLocaleString()}
                      <span className="text-xs font-normal text-slate-400 ml-1">calls/mo</span>
                    </div>
                    <span className="text-[10px] text-slate-400 font-medium">
                      {calculation.hoursMonthly.toLocaleString()} dialer hours
                    </span>
                  </div>
                </div>
              </div>

              {/* Call to Action Button */}
              <button
                type="button"
                onClick={() =>
                  onOpenProposalModal({
                    campaignId: selectedCampaignId,
                    seatsCount,
                    shiftType,
                    monthlyRetainer: calculation.monthlyTotal,
                  })
                }
                className="w-full py-3.5 px-6 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold text-sm tracking-wide shadow-lg shadow-cyan-500/25 flex items-center justify-center gap-2 group transition-all"
              >
                <span>Lock Pod Proposal &amp; Schedule Discovery Call</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>

              <div className="flex items-center justify-center gap-4 mt-3 text-[11px] text-slate-400">
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400" /> Instant PDF Proposal
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400" /> Dedicated Team Lead
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400" /> 14-Day Pilot Guarantee
                </span>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
